import { t } from "../i18n";

export type GardenLocationTarget = {
  graveId?: string;
  x?: number;
  y?: number;
  holeId?: string;
  openModal?: boolean;
};

const MEMORIAL_PATH = /^\/memorial\/([^/]+)\/?$/;
const PRESERVED_QUERY_KEYS = ["time"] as const;
const FOCUS_QUERY_KEYS = ["grave", "headstone", "hole", "x", "y"] as const;

let applyingGardenLocation = 0;

export function isApplyingGardenLocation(): boolean {
  return applyingGardenLocation > 0;
}

export function runAsGardenLocationApply(fn: () => void): void {
  applyingGardenLocation += 1;
  try {
    fn();
  } finally {
    applyingGardenLocation -= 1;
  }
}

export function gardenMemorialPath(graveId: string): string {
  return `/memorial/${encodeURIComponent(graveId)}`;
}

export function parseGardenLocation(
  params?: URLSearchParams,
): GardenLocationTarget | null {
  if (typeof window !== "undefined") {
    const match = window.location.pathname.match(MEMORIAL_PATH);
    if (match?.[1]) {
      try {
        return { graveId: decodeURIComponent(match[1]), openModal: true };
      } catch {
        return { graveId: match[1], openModal: true };
      }
    }
  }

  const search =
    params ??
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams());

  const graveId = search.get("grave") || search.get("headstone");
  if (graveId) return { graveId, openModal: true };

  const holeId = search.get("hole");
  if (holeId) return { holeId, openModal: false };

  const xRaw = search.get("x");
  const yRaw = search.get("y");
  if (xRaw !== null && yRaw !== null) {
    const x = parseInt(xRaw, 10);
    const y = parseInt(yRaw, 10);
    if (!Number.isNaN(x) && !Number.isNaN(y)) {
      return { x, y, openModal: false };
    }
  }
  return null;
}

export function setMemorialViewLocation(graveId: string): void {
  if (!graveId || isApplyingGardenLocation()) return;
  const params = preservedSearchParams(
    new URLSearchParams(window.location.search),
  );
  const href = hrefFrom(gardenMemorialPath(graveId), params);
  const method = shouldPushMemorialUrl() ? "pushState" : "replaceState";
  commitGardenUrl(href, method, { garden: "memorial", graveId });
}

export function setGardenFocusLocation(x: number, y: number): void {
  if (isApplyingGardenLocation()) return;
  const params = preservedSearchParams(
    new URLSearchParams(window.location.search),
  );
  params.set("x", String(x));
  params.set("y", String(y));
  commitGardenUrl(hrefFrom("/", params), "replaceState", {
    garden: "focus",
    x,
    y,
  });
}

export function clearGardenFocusLocation(): void {
  if (isApplyingGardenLocation()) return;
  if (MEMORIAL_PATH.test(window.location.pathname)) return;
  if (window.location.pathname !== "/") return;
  const params = new URLSearchParams(window.location.search);
  const hasFocus = FOCUS_QUERY_KEYS.some((key) => params.has(key));
  if (!hasFocus) return;
  commitGardenUrl(
    hrefFrom("/", preservedSearchParams(params)),
    "replaceState",
    { garden: "home" },
  );
}

export function syncGardenDocumentTitle(memorialName?: string | null): void {
  document.title = memorialName
    ? t("share.memorialTitle", { name: memorialName })
    : t("seo.title");
}

function preservedSearchParams(from: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of PRESERVED_QUERY_KEYS) {
    const value = from.get(key);
    if (value) next.set(key, value);
  }
  return next;
}

function hrefFrom(pathname: string, search: URLSearchParams): string {
  const query = search.toString();
  return pathname + (query ? `?${query}` : "") + window.location.hash;
}

function currentHref(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

function commitGardenUrl(
  href: string,
  method: "pushState" | "replaceState",
  state: Record<string, unknown>,
): void {
  if (currentHref() === href) return;
  window.history[method](state, "", href);
}

function shouldPushMemorialUrl(): boolean {
  if (MEMORIAL_PATH.test(window.location.pathname)) return false;
  const params = new URLSearchParams(window.location.search);
  return !(params.get("grave") || params.get("headstone"));
}
